import { useEffect, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, X } from 'lucide-react';

/**
 * The Figma connect-modal shell (564px, radius 16, 24px padding/gap): portal + scrim + panel +
 * header (title + bordered 32px close) + Escape-to-close — extracted from the bespoke shells the
 * CQG/Rithmic connector dialogs carry inline, for the Add-account dialog (and future connect
 * surfaces) to compose. The legacy dialogs keep their own inline shells for now (their titles/close
 * visibility are state-dependent); folding them onto this shell is a later cleanup.
 */
export function ConnectModalShell({
  open, onClose, title, ariaLabel, children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  ariaLabel: string;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--scrim)' }}
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={e => e.stopPropagation()}
        className="flex w-full max-w-[564px] flex-col gap-6 rounded-2xl bg-canvas-raised p-6 shadow-card"
        style={{ border: '1px solid var(--canvas-divider)' }}
      >
        <header className="flex items-center justify-between gap-2.5">
          <h2 className="text-base font-bold leading-[19px] text-text">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-lg border bg-canvas-raised text-text-muted transition-colors hover:text-text"
            style={{ borderColor: 'var(--canvas-divider)' }}
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/**
 * The Figma Add-account text input: 48px pill (radius 32), 1.5px divider border, leading icon,
 * 15px/0.75-tracked placeholder at 50% muted. `type="password"` fields get a trailing show/hide
 * eye toggle (2026-07-18 user direction). (The legacy dialogs' inputs are the 12px-radius variant.)
 */
export function PillInput({
  icon, value, onChange, type, ...rest
}: {
  icon: ReactNode;
  value: string;
  onChange: (v: string) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  return (
    <label className="flex h-12 items-center gap-3 rounded-full border-[1.5px] border-[color:var(--canvas-divider)] bg-canvas-raised px-3 transition-colors focus-within:border-[color:var(--accent)]">
      <span className="text-text-muted">{icon}</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        type={isPassword && revealed ? 'text' : type}
        className="w-full bg-transparent text-[15px] tracking-[0.75px] text-text outline-none placeholder:text-text-muted placeholder:opacity-50"
        {...rest}
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setRevealed(r => !r)}
          aria-label={revealed ? 'Hide password' : 'Show password'}
          className="text-text-muted transition-colors hover:text-text"
        >
          {revealed ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      )}
    </label>
  );
}

/** Pill select matching {@link PillInput} — the Add-account system dropdown. */
export function PillSelect({
  icon, value, onChange, children, ...rest
}: {
  icon: ReactNode;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'>) {
  return (
    <label className="flex h-12 items-center gap-3 rounded-full border-[1.5px] border-[color:var(--canvas-divider)] bg-canvas-raised px-3 transition-colors focus-within:border-[color:var(--accent)]">
      <span className="text-text-muted">{icon}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full cursor-pointer bg-transparent text-[15px] text-text outline-none"
        {...rest}
      >
        {children}
      </select>
    </label>
  );
}
